// How long a competition takes on stage (card a1f0fff5). One formula for every
// event with fixed constants, and every entered dancer is counted (Devin
// 2026-10-05). Fitted on what Crown and Rose 2026 actually took and checked
// against published feis and Oireachtas schedules (research
// research-time-estimates.md and -round2.md). Option E (Devin 2026-10-05):
// grades dance 32 bars, the hornpipe counts 4 beats a bar, changeover 18 s
// (grades) / 20 s (championships), 60 s a competition; teams are counted in
// teams; traditional sets and St. Patrick's Day go 2 at a time and
// contemporary/championship set dances one at a time (2026-10-28, see below). Admin copy of the web
// app's file (card a1f0fff5): keep the two identical.
//
// One round (grades, specials, teams, set dances):
//   60 s + ceil(N / at a time) x (music + 18 s)      N = teams for a figure
// Championships (2 or 3 rounds; dancers at a time 2, 2, 1 unless changed):
//   60 s + ceil(N / k1) x (97 s + 20 s)             round 1, heavy shoe
//        + 270 s + ceil(N / k2) x (68 s + 20 s)       round 2, light shoe
//      [ + 270 s + ceil(N / k3) x (90 s + 20 s) ]     round 3, set dance
// Music = (bars + 8 introduction bars) x beats per bar / speed, in seconds.

export const C = {
  COMP: 60, // call, line-up, sheets to the table
  ROUND: 270, // recall announcement and round change
  W_GRADE: 18, // changeover between groups, one-round competitions
  W_CHAMP: 20, // changeover between groups, championships
  HEAVY: 97, // 48-bar treble jig @73 (92 s) and 40-bar hornpipe @113 (102 s), averaged
  SOFT: 68, // 48-bar reel @113 and 40-bar slip jig @113, averaged
  GRADE_BARS: 32, // grade competitions dance two steps
  SET: 90, // a set dance
  INTRO: 8, // bars of introduction before each group
  UNKNOWN: 60, // a dance we can't name: about a reel
};

const musicSec = (bars, speed, beats = 2) => ((bars + C.INTRO) * beats * 60) / speed;
const SLOWER = /beginner|first feis/i; // Beginner, Advanced Beginner, First Feis dance at the faster grade speeds
const CHOICE = /novice|prizewinner|adult/i; // treble jig and hornpipe speed is the dancer's choice: averaged

// The dances, most specific first. Speeds from the IDTAC-ER mini-feis and
// ECRO 2026 syllabi; grades dance 32 bars (two steps). A hornpipe bar counts
// 4 beats (a 40-bar hornpipe @113 runs ~110 s on real tracks). Sets and team
// figures are set lengths; a set dance is danced one at a time.
const DANCES = [
  { re: /treble\s*reel/i, label: 'Treble Reel', solo: true, sec: () => musicSec(16 - C.INTRO, 113), music: () => '16 bars @ 113 each' },
  // traditional sets and St. Patrick's Day go up 2 (or 3) at a time; contemporary and championship sets one at a
  // time (Devin 2026-10-28; side-stage check-in timing at 19 events: sets 1.09x the formula at 2, so not 3)
  { re: /st\.?\s*patrick/i, label: "St. Patrick's Day", sec: () => 66, music: () => 'set, about 66 s' },
  {
    re: /contemporary|own\s*choice|\bchoice set|non.?trad|(prelim|open|champ)\w*\s*set/i,
    label: 'Set Dance',
    solo: true,
    sec: () => C.SET,
    music: () => 'set, about 90 s',
  },
  { re: /trad.*set|traditional|\bset\b/i, label: 'Traditional Set', sec: () => 66, music: () => 'set, about 66 s' },
  {
    re: /treble\s*jig|heavy\s*jig|\bt\.?\s*j\b/i,
    label: 'Treble Jig',
    sec: (lv) => (SLOWER.test(lv) ? musicSec(C.GRADE_BARS, 92) : CHOICE.test(lv) ? (musicSec(C.GRADE_BARS, 92) + musicSec(C.GRADE_BARS, 73)) / 2 : musicSec(C.GRADE_BARS, 73)),
    music: (lv) => (SLOWER.test(lv) ? '32 bars @ 92' : CHOICE.test(lv) ? '32 bars @ 92 or 73' : '32 bars @ 73'),
  },
  {
    re: /hornpipe/i,
    label: 'Hornpipe',
    sec: (lv) => (SLOWER.test(lv) ? musicSec(C.GRADE_BARS, 138, 4) : CHOICE.test(lv) ? (musicSec(C.GRADE_BARS, 138, 4) + musicSec(C.GRADE_BARS, 113, 4)) / 2 : musicSec(C.GRADE_BARS, 113, 4)),
    music: (lv) => (SLOWER.test(lv) ? '32 bars @ 138' : CHOICE.test(lv) ? '32 bars @ 138 or 113' : '32 bars @ 113'),
  },
  {
    re: /slip\s*jig/i,
    label: 'Slip Jig',
    sec: (lv) => musicSec(C.GRADE_BARS, SLOWER.test(lv) ? 122 : 113, 3),
    music: (lv) => `32 bars @ ${SLOWER.test(lv) ? 122 : 113}`,
  },
  { re: /single\s*jig/i, label: 'Single Jig', sec: () => musicSec(C.GRADE_BARS, 124), music: () => '32 bars @ 124' },
  { re: /light\s*jig/i, label: 'Light Jig', sec: () => musicSec(C.GRADE_BARS, 115), music: () => '32 bars @ 115' },
  { re: /\breel\b/i, label: 'Reel', sec: (lv) => musicSec(C.GRADE_BARS, SLOWER.test(lv) ? 122 : 113), music: (lv) => `32 bars @ ${SLOWER.test(lv) ? 122 : 113}` },
];

// Team figures: teams on stage at once and the length of the figure.
// Explicit hand counts first; a céilí with no count is an 8-hand (web
// review R1-F9: "2-Hand Céilí" is a 2-hand).
const FIGURES = [
  { re: /\b8[\s-]*hand/i, label: '8-Hand', sec: 180, k: 1, hands: 8 },
  { re: /\b6[\s-]*hand/i, label: '6-Hand', sec: 150, k: 1, hands: 6 },
  { re: /\b4[\s-]*hand/i, label: '4-Hand', sec: 120, k: 1, hands: 4 },
  { re: /\b3[\s-]*hand/i, label: '3-Hand', sec: 75, k: 2, hands: 3 },
  { re: /\b2[\s-]*hand/i, label: '2-Hand', sec: 60, k: 2, hands: 2 },
  { re: /ceili|céilí/i, label: '8-Hand', sec: 180, k: 1, hands: 8 },
];

const text = (c) => [c?.description, c?.name, c?.code].filter(Boolean).join(' ');

// Championship-style: more than one round (prelims and opens).
export const isChampionship = (c) =>
  Number(c?.rounds) > 1 ||
  c?.championship === true ||
  /PRELIM|OPEN CHAMP/i.test(String(c?.group || '')) ||
  /^\s*(prelim|open champ)/i.test(String(c?.description || ''));

export const roundsOf = (c) => {
  const r = Number(c?.rounds);
  if (r >= 1 && r <= 3) return r;
  if (!isChampionship(c)) return 1;
  return /OPEN/i.test(`${c?.group} ${c?.description}`) ? 3 : 2;
};

export const figureOf = (c) => FIGURES.find((f) => f.re.test(text(c))) || null;

export const danceOf = (c) => {
  const t = text(c);
  const fig = figureOf(c);
  if (fig) return { label: fig.label, sec: fig.sec, music: `figure, about ${fig.sec} s`, team: true, k: fig.k, hands: fig.hands };
  const level = String(c?.group || '');
  const d = DANCES.find((x) => x.re.test(t));
  if (!d) return { label: '', sec: C.UNKNOWN, music: 'dance not named: about a reel', unknown: true };
  return { label: d.label, sec: d.sec(level), music: d.music(level), solo: Boolean(d.solo) };
};

const DEFAULT_CHAMP_K = [2, 2, 1];

// Dancers (or teams) on stage at once in a round (1-based): the schedule's
// own choice, else the syllabus setting, else the default.
export const atATime = (c, round, override) => {
  // the syllabus numbers rounds from 1 ({ round: 1, numberOfDancers: 2 })
  const pick = (list) => {
    const hit = (list || []).find((d) => Number(d?.round) === round);
    const n = Number(hit?.numberOfDancers);
    return n >= 1 && n <= 9 ? Math.floor(n) : null;
  };
  const chosen = pick(override) ?? pick(c?.dancerCount);
  if (chosen) return chosen;
  const d = danceOf(c);
  // a one-round set dance (a prelim set, say) is danced one at a time
  if (roundsOf(c) === 1 && d.solo) return 1;
  if (isChampionship(c)) return DEFAULT_CHAMP_K[round - 1] || 1;
  if (d.team) return d.k;
  if (d.solo) return 1;
  return 2;
};

const dancersOf = (c) => (Array.isArray(c?.dancers) ? c.dancers.length : Number(c?.dancerTotal) || 0);

// The estimate with its parts, for the timing panel. Seconds throughout.
export const estimate = (c, override) => {
  const n = dancersOf(c);
  const rounds = roundsOf(c);
  if (n === 0) return { n, rounds, totalSec: 0, parts: [], overheadSec: 0, minutes: 0 };
  const parts = [];
  if (rounds === 1) {
    const d = danceOf(c);
    const k = atATime(c, 1, override);
    // a figure's entries are its dancers: on stage it is teams (option E)
    const teams = d.team && d.hands ? Math.ceil(n / d.hands) : null;
    const groups = Math.ceil((teams ?? n) / k);
    parts.push({ round: 1, dance: d.label || 'Dance', music: d.music, perGroupSec: d.sec, k, groups, teams, sec: groups * (d.sec + C.W_GRADE) });
  } else {
    const champRounds = [
      { dance: 'Heavy shoe', music: 'treble jig or hornpipe, about 97 s', sec: C.HEAVY },
      { dance: 'Light shoe', music: 'reel or slip jig, about 68 s', sec: C.SOFT },
      { dance: 'Set dance', music: 'about 90 s', sec: C.SET },
    ];
    for (let r = 1; r <= rounds; r += 1) {
      const def = champRounds[r - 1];
      const k = atATime(c, r, override);
      const groups = Math.ceil(n / k);
      parts.push({ round: r, dance: def.dance, music: def.music, perGroupSec: def.sec, k, groups, sec: groups * (def.sec + C.W_CHAMP) });
    }
  }
  const overheadSec = C.COMP + (rounds - 1) * C.ROUND;
  const totalSec = overheadSec + parts.reduce((s, p) => s + p.sec, 0);
  return { n, rounds, parts, overheadSec, totalSec, minutes: Math.max(1, Math.round(totalSec / 60)) };
};

export const minutesFor = (c, override) => estimate(c, override).minutes;

// ---- clock ---------------------------------------------------------------
// Times are minutes from midnight; a day can run past midnight (1440+).

// "8:30 AM", "08:30 am", "8:30", "20:30" -> minutes; null when unreadable
export const parseTime = (s) => {
  const m = /^\s*(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?\s*$/i.exec(String(s || ''));
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  const ap = (m[3] || '').toLowerCase().replace(/\./g, '');
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return h * 60 + min;
};

const hm = (t) => {
  const d = ((Math.round(t) % 1440) + 1440) % 1440;
  return [Math.floor(d / 60), d % 60];
};
// "8:45 AM" (what the schedule stores for check-in, results and the app)
export const formatTime = (t) => {
  const [h, m] = hm(t);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
// "8:45" on a row; "8:45 am" in a header
export const shortTime = (t) => {
  const [h, m] = hm(t);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')}`;
};
export const clockTime = (t) => formatTime(t).toLowerCase();
// "08:45" for <input type="time">
export const inputTime = (t) => {
  const [h, m] = hm(t);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
// "6m", "2h18"
export const shortDuration = (min) => {
  const m = Math.max(0, Math.round(min));
  return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : `${m}m`;
};
export const longDuration = (min) => {
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  return m % 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${Math.floor(m / 60)} h`;
};

// The running order of one stage: each item starts when the one before ends,
// unless it is pinned to a fixed start. `minutesOf(item)` gives its length.
// Returns, per item, { start, end, gap (idle minutes before a fixed start),
// overlap (minutes a fixed start cuts into the one before) }.
export const flow = (items, stageStart, minutesOf) => {
  let t = stageStart;
  return (items || []).map((item, i) => {
    let start = t;
    let gap = 0;
    let overlap = 0;
    if (item.fixed && Number.isFinite(item.fixedAt)) {
      // a fixed start earlier than the clock means the day passed midnight
      let at = item.fixedAt;
      while (i > 0 && at < t - 720) at += 1440;
      start = at;
      if (i > 0) {
        if (at > t) gap = at - t;
        else if (at < t) overlap = t - at;
      }
    }
    const len = Math.max(0, minutesOf(item));
    const end = start + len;
    t = end;
    return { start, end, gap, overlap, minutes: len };
  });
};
